import React, { useEffect, useState, useContext } from 'react';

import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { Box, Button, Chip, MenuItem, MenuList } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { AiOutlinePlus } from 'react-icons/ai';
import clsx from 'clsx';
import { HiOutlineTrash } from 'react-icons/hi';

import {
  AppDataGridWithSavedPage,
  FilterSystem,
  GridCellWithAvatar,
  ActivateSubscriptionDialog,
  ModuleOnboardingChips,
} from '@vyce/core/src/components';
import { ChatUser, FilterSection, CompanyWorkerItem } from '@vyce/core/src/types';
import { GRID_PAGE_SIZE, TABLE_OFFSET_DELAY } from '@vyce/core/src/constants';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { ListCompanyWorkersRequest } from '@vyce/core/src/api/types';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { GridActions } from '@vyce/core/src/components';
import { DeviceContext } from '@vyce/core/src/contexts';
import { isNil } from '@vyce/core/src/utils';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { useTable } from '@vyce/core/src/hooks/useTable';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';

import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { MANAGEMENT_PERMISSIONS } from '@vyce/core/src/types';

import { useTypedSelector, useInviteButton } from 'src/hooks';
import { UniversalInviteModule } from 'src/modules';

interface Props {
  users: CompanyWorkerItem[];
  loading: boolean;
  getUsers: (params: ListCompanyWorkersRequest) => void;
  handleRemoveUserClick: (user: CompanyWorkerItem) => void;
  total: number;
  moduleName: string;
  filtersSections?: FilterSection[];
  prepareFilters?: (filters: any) => Partial<ListCompanyWorkersRequest>;
  showRemoveAction?: boolean;
  removeActionLabel?: string;
}

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'asc' }];

export const UserList: React.FC<Props> = ({
  users,
  loading,
  getUsers,
  total,
  handleRemoveUserClick,
  moduleName,
  filtersSections,
  prepareFilters,
  showRemoveAction = true,
  removeActionLabel,
}) => {
  const horizontalScrollClasses = useHorizontalScrollStyles();
  const history = useHistory();
  const {
    sortModel,
    offset,
    substring,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });

  const { selectedCompany } = useTypedSelector(state => state.helper);
  const { user } = useTypedSelector(state => state);

  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const [filters, setFilters] = useState<Partial<ListCompanyWorkersRequest>>({});
  const { isMobile } = useContext(DeviceContext);
  const { createPrivateChat } = useChatRequests();

  const chatCreator: ChatUser = {
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    phone: user.phone,
    avatarUrl: user.photo?.url,
  };

  const goToChat = async (worker: CompanyWorkerItem) => {
    if (!selectedCompany?.uuid) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: worker.user_id,
        name: `${worker.first_name} ${worker.last_name}`,
        avatarUrl: worker.avatar ?? undefined,
      },
      companyId: selectedCompany.uuid,
    });
  };

  const handleFilterChange = (newFilters: any) => {
    setFilters(prepareFilters ? prepareFilters(newFilters) : {});
  };

  const dOffset = useDebounceValue(offset, TABLE_OFFSET_DELAY);

  const {
    isInviteFormOpen,
    subscribeLoading,
    isActivateSubscriptionDialogOpen,
    simpleSubscribe,
    setCloseInviteForm,
    handleInviteButtonClick,
    closeActivateSubscriptionDialog,
    canInvite,
  } = useInviteButton();
  const { can } = usePermissions();
  const canManageWorkerAccounts = can(MANAGEMENT_PERMISSIONS.MANAGE_WORKER_ACCOUNTS);

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      flex: 0.1,
      minWidth: 200,
      disableColumnMenu: true,
      renderCell: params => {
        const row = params.row as CompanyWorkerItem;
        return (
          <GridCellWithAvatar
            createPrivateChat={() => goToChat(row)}
            avatarUrl={row.avatar}
            name={`${row.first_name} ${row.last_name}`}
            link={`${history.location.pathname}/${row.first_name}_${row.user_id}`}
          />
        );
      },
    },
    {
      field: 'ni_number',
      headerName: 'Ref #',
      width: 110,
      flex: 0.1,
      disableColumnMenu: true,
    },
    {
      // The field doubles as the server sort key: formatSortModel sends it as order_by.field_name
      field: 'onboarding_completion',
      headerName: 'Modules & Onboarding',
      flex: 0.15,
      minWidth: 280,
      disableColumnMenu: true,
      renderCell: params => {
        const row = params.row as CompanyWorkerItem;
        return <ModuleOnboardingChips modules={row.modules} onboarding={row.onboarding} />;
      },
    },
    {
      field: '',
      headerName: 'Actions',
      width: 80,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params =>
        // Removing someone from a module is a worker-account action, not a
        // module-level one, so it needs the company-wide management right.
        showRemoveAction && canManageWorkerAccounts ? (
          <Box display="flex" width="100%">
            <GridActions close={closeGridAction}>
              <MenuList>
                <MenuItem
                  cy-test-id="remove-from-module"
                  onClick={() => handleRemoveClick(params.row as CompanyWorkerItem)}>
                  <Box display="flex" alignItems="center" gridGap={8}>
                    <HiOutlineTrash size={14} /> {removeActionLabel ?? `Remove from ${moduleName} module`}
                  </Box>
                </MenuItem>
              </MenuList>
            </GridActions>
          </Box>
        ) : null,
    },
  ];

  const handleRemoveClick = (user: CompanyWorkerItem) => {
    resetCloseStatus();
    handleRemoveUserClick(user);
  };

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  useEffect(() => {
    if (isNil(dOffset)) return;

    getUsers({
      substring,
      offset: dOffset as number,
      limit: GRID_PAGE_SIZE,
      order_by: formatSortModel<CompanyWorkerItem>(sortModel),
      ...filters,
    });
  }, [selectedCompany, substring, dOffset, sortModel, filters, getUsers]);

  return (
    <>
      <Box
        marginBottom={2}
        gridGap={16}
        className={clsx(horizontalScrollClasses.blockWrapper, horizontalScrollClasses.blockWithHideScroll)}>
        <Box>
          <AppSearchInput onChange={handleSearchChange} isSmall smallRounding expanded={!isMobile} />
        </Box>
        <Box display="flex" gridGap={16}>
          {filtersSections && (
            <FilterSystem filtersSections={filtersSections} onFiltersChange={handleFilterChange} />
          )}
          {canInvite && (
            <Button
              onClick={handleInviteButtonClick}
              size="small"
              data-tour="invite"
              startIcon={<AiOutlinePlus />}
              variant="contained"
              color="primary">
              Invite New Members
            </Button>
          )}
        </Box>
      </Box>

      <AppDataGridWithSavedPage
        noPaper
        rows={users}
        getRowId={row => row.user_id}
        height="calc(100vh - 240px)"
        columns={columns}
        rowCount={total}
        loading={loading}
        pageSize={GRID_PAGE_SIZE}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        disableSelectionOnClick
        unit="users"
        withProfileButton
        setOffset={setOffset}
      />
      <UniversalInviteModule isOpen={isInviteFormOpen} onClose={setCloseInviteForm} />
      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={simpleSubscribe}
        loading={subscribeLoading}
        wasSubscribed={selectedCompany?.subscribed_company}
      />
    </>
  );
};
